import { DuplicateDebtor } from "us.collection.debtor/Interfaces";
import _ from "lodash";

/**
 * @description Add or remove selected duplicating debtors.
 * @param {Number} entRoleId - Selected debtor entRoleId.
 * @param {Array<Number>} selectedDebtors - Previously selected debtors.
 * @returns Return updated array.
 */
export const handleDebtorSelection = (
  entRoleId: number,
  selectedDebtors: number[]
): number[] => {
  try {
    if (entRoleId > 0) {
      if (_.includes(selectedDebtors, entRoleId)) {
        return _.without(selectedDebtors, entRoleId);
      } else {
        return [...selectedDebtors, entRoleId];
      }
    } else {
      return selectedDebtors;
    }
  } catch (e) {
    return selectedDebtors;
  }
};

/**
 * @description Remove unnecessary data
 * @param {string} text - Text to be cleaned up
 * @returns {string} - cleaned text
 */
export const clearText = (text: string): string => {
  try {
    return text?.trim()?.toLowerCase();
  } catch (error) {
    return "";
  }
};

/**
 * @description Returns property names of missing data.
 * @param {DuplicateDebtor} retainingDebtor - Selected retaining debtor entRoleId.
 * @param {DuplicateDebtor} currentDebtor - All duplicate debtors.
 * @returns Return updated array.
 */
export const getMismatchingFields = (
  retainingDebtor: any,
  currentDebtor: any
): string[] => {
  try {
    const { lastName } = currentDebtor ?? {};
    let mismatchingFields: any = [];

    Object.keys(retainingDebtor)?.map((key) => {
      if (key == "firstName") {
        const retainingFullName =
          clearText(retainingDebtor?.[key]) +
          clearText(retainingDebtor?.lastName);
        const debtorFullName =
          clearText(currentDebtor?.[key]) + clearText(lastName);

        if (retainingFullName !== debtorFullName || !debtorFullName) {
          mismatchingFields.push("name");
        }
      } else {
        if (
          clearText(retainingDebtor?.[key]) !==
            clearText(currentDebtor?.[key]) ||
          currentDebtor?.[key] == "0001-01-01T00:00:00" ||
          !currentDebtor?.[key]?.toString()?.trim()
        ) {
          mismatchingFields.push(key);
        }
      }
    });
    return mismatchingFields;
  } catch (e) {
    return [];
  }
};

/**
 * @description Adds property to get all mismatching fields.
 * @param {Array<Number>} duplicatingDebtors - All duplicate debtors.
 * @returns Return updated array.
 */
export const setMismatchingFields = (
  duplicatingDebtors: DuplicateDebtor[]
): DuplicateDebtor[] => {
  try {
    if (Array.isArray(duplicatingDebtors)) {
      const retainingDebtor = _.find(duplicatingDebtors, {
        isSelectedRetaining: true,
      });

      if (retainingDebtor) {
        return duplicatingDebtors?.map((debtor) => {
          return {
            ...debtor,
            mismatchingFields: getMismatchingFields(retainingDebtor, debtor),
          };
        });
      } else {
        return duplicatingDebtors?.map((debtor) => ({
          ...debtor,
          mismatchingFields: [],
        }));
      }
    } else {
      return [];
    }
  } catch (e) {
    return duplicatingDebtors;
  }
};

/**
 * @description Add or remove retaining debtor.
 * @param {Number} entRoleId - Selected retaining debtor entRoleId.
 * @param {Array<DuplicateDebtor>} duplicatingDebtors - All duplicate debtors.
 * @returns Return updated array.
 */
export const selectRetainingDebtor = (
  entRoleId: number,
  duplicatingDebtors: DuplicateDebtor[]
): DuplicateDebtor[] => {
  try {
    return setMismatchingFields(
      duplicatingDebtors?.map((debtor) => {
        if (debtor?.entRoleId == entRoleId) {
          return {
            ...debtor,
            isSelectedRetaining: debtor?.isSelectedRetaining ? false : true,
          };
        } else {
          return {
            ...debtor,
            isSelectedRetaining: false,
          };
        }
      })
    );
  } catch (e) {
    return duplicatingDebtors;
  }
};

/**
 * @description Format & sort duplicate debtor payload.
 * @param {Array<DuplicateDebtor>} duplicatingDebtors - Endpoint response.
 * @param {Number} caseDebtorEntRoleId - The ent id of the case debtor
 * @returns Return updated array.
 */
export const formatDuplicateDebtors = (
  duplicatingDebtors: DuplicateDebtor[],
  caseDebtorEntRoleId: number
): DuplicateDebtor[] => {
  try {
    if (Array.isArray(duplicatingDebtors)) {
      let sortedDebtors = _.sortBy(duplicatingDebtors, [
        (debtor) => !debtor.isRetainingDebtor,
      ]);

      //Find the index of the case debtor and move it after the isRetainingDebtor:true records
      const index = _.findIndex(sortedDebtors, {
        entRoleId: caseDebtorEntRoleId,
      });
      if (index > -1) {
        const selectedDebtor = sortedDebtors.splice(index, 1)[0];

        // Find the last index of isRetainingDebtor: true
        const lastTrueIndex = _.findLastIndex(sortedDebtors, {
          isRetainingDebtor: true,
        });

        // Insert the case debtor after the last true retaining debtor
        sortedDebtors.splice(lastTrueIndex + 1, 0, {
          ...selectedDebtor,
          isCaseDebtor: true,
        });
      }

      return sortedDebtors;
    } else {
      return [];
    }
  } catch (e) {
    return duplicatingDebtors;
  }
};

/**
 * @description Format & sort duplicate debtor payload.
 * @param {Array<DuplicateDebtor>} duplicatingDebtors - Endpoint response.
 * @param {Number} caseDebtorEntRoleId - The ent id of the case debtor
 * @returns Return updated array.
 */
export const formatAdditionalInfoMetaData = (
  metaDataList: any[],
  category: string
): { statusOptions: any[]; infoOptions: any[] } => {
  try {
    if (Array.isArray(metaDataList)) {
      const filteredList = metaDataList?.filter(
        ({ fieldCategory }: any) => fieldCategory == category
      );

      let { valueSource } = _.find(filteredList, {
        fieldName: "DebtorStatus",
      });

      let statusOptions = [];
      let infoOptions = [];

      if (valueSource) {
        statusOptions = valueSource.split(",")?.map((value: string) => ({
          label: value?.trim(),
          value: value?.trim(),
        }));
      }

      infoOptions = filteredList
        ?.filter(({ fieldName }) => fieldName !== "DebtorStatus")
        ?.map((item) => ({
          ...item,
          label: item.fieldName,
          value: item.fieldName,
        }));

      return { statusOptions, infoOptions };
    } else {
      return { statusOptions: [], infoOptions: [] };
    }
  } catch (e) {
    return { statusOptions: [], infoOptions: [] };
  }
};
